import { useCallback, useEffect, useRef } from "react";

/**
 * Hook for managing abortable async requests.
 * Automatically cancels pending requests when a new one starts or on unmount.
 *
 * @returns executeRequest function that accepts an async callback with AbortSignal
 */
export function useAbortableRequest() {
	const abortControllerRef = useRef<AbortController | null>(null);

	const executeRequest = useCallback(
		async <T>(requestFn: (signal: AbortSignal) => Promise<T>): Promise<T | undefined> => {
			// Cancel any pending request
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			// Create new abort controller for this request
			const abortController = new AbortController();
			abortControllerRef.current = abortController;

			try {
				const result = await requestFn(abortController.signal);

				// Only return result if this request wasn't cancelled
				if (!abortController.signal.aborted) {
					return result;
				}
			} catch (error) {
				// Ignore abort errors, rethrow others if request is still active
				if (!abortController.signal.aborted) {
					throw error;
				}
			} finally {
				// Clean up if this is still the current controller
				if (abortControllerRef.current === abortController) {
					abortControllerRef.current = null;
				}
			}

			return undefined;
		},
		[],
	);

	// Cleanup abort controller on unmount
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, []);

	return { executeRequest };
}
